'use client'

/* eslint-disable @next/next/no-img-element */
import { cn } from '@gyym/ui'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { ModalLogo } from '../_data'
import { CloseIcon } from './icons'

interface LpModalProps {
  open: boolean
  onClose: () => void
  /** ブランド紹介モーダルは下マージンが 15px (Studio 版の値) */
  variant?: 'activity' | 'brand'
  labelId: string
  children: React.ReactNode
}

/**
 * Studio 版のモーダルの再現。
 * 半透明の紺のカード (opacity 0.9) が下からスライド + フェードインで開く。
 * 背景クリック / Escape / 閉じるボタンで閉じる。
 */
export const LpModal = ({
  open,
  onClose,
  variant = 'activity',
  labelId,
  children,
}: LpModalProps) => {
  const [mounted, setMounted] = useState(false)
  const [shown, setShown] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!open) {
      setShown(false)
      return
    }
    restoreFocusRef.current = document.activeElement as HTMLElement | null
    // 1 フレーム後に表示状態へ切り替えて transition を発火させる
    const frame = requestAnimationFrame(() => {
      setShown(true)
      closeRef.current?.focus()
    })
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKeyDown)
      restoreFocusRef.current?.focus()
    }
  }, [open, onClose])

  if (!mounted || !open) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
      className="fixed inset-0 z-[100] overflow-y-auto"
    >
      <button
        type="button"
        aria-label="閉じる"
        onClick={onClose}
        className="fixed inset-0 cursor-default bg-black/[0.16]"
      />
      <div className="pointer-events-none flex min-h-full flex-col items-center justify-center">
        <div
          className={cn(
            'pointer-events-auto relative flex w-[600px] max-w-[calc(100%-40px)] flex-col items-center rounded-[10px] border-2 border-solid border-[#FF01AB] bg-[#0E0D22] pb-5 pt-[30px] shadow-[0_6px_15px_rgba(0,0,0,0.2)] transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.4,0.4,0,1)]',
            variant === 'brand' ? 'mx-5 mb-[15px] mt-5' : 'm-5',
            shown
              ? 'translate-y-0 opacity-90'
              : 'translate-y-[100px] opacity-0',
          )}
        >
          <button
            ref={closeRef}
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="absolute right-5 top-5 flex size-6 items-center justify-center text-[#EEEEEE]"
          >
            <CloseIcon className="size-6" />
          </button>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** モーダル内の「写真 + ロゴ」の 2 分割行 (高さ 160px) */
export const ModalPhotoRow = ({
  photo,
  photoPosition,
  logo,
  className,
}: {
  photo: string
  photoPosition?: string
  logo: ModalLogo
  className?: string
}) => (
  <div
    className={cn(
      'flex w-[calc(100%-30px)] flex-wrap items-start bg-[#EEEEEE]',
      className,
    )}
  >
    <div
      className="h-40 w-1/2 bg-cover"
      style={{
        backgroundImage: `url(${photo})`,
        backgroundPosition: photoPosition ?? '50% 50%',
      }}
    />
    <div
      className="flex h-40 w-1/2 items-center justify-center bg-cover bg-center"
      style={{
        backgroundColor: logo.background,
        ...(logo.cover ? { backgroundImage: `url(${logo.src})` } : {}),
      }}
    >
      {!logo.cover && (
        <img
          src={logo.src}
          alt=""
          className={cn(
            'max-w-full object-contain',
            logo.spWidth !== undefined && 'max-[540px]:hidden',
          )}
          style={{ width: logo.width ?? 110, height: logo.height ?? 110 }}
        />
      )}
      {!logo.cover && logo.spWidth !== undefined && (
        <img
          src={logo.src}
          alt=""
          className="hidden max-w-full object-contain max-[540px]:block"
          style={{ width: logo.spWidth, height: logo.spHeight }}
        />
      )}
    </div>
  </div>
)

/** 配列の要素ごとに <br> で区切って描画する */
export const Lines = ({ lines }: { lines: string[] }) =>
  lines.map((line, i) => (
    <span key={line}>
      {i > 0 && <br />}
      {line}
    </span>
  ))
